import { useState } from 'react';
import { PropTypes } from 'prop-types';
import Constant from '@/common/constant/Constant';
import trI18n from '@/common/utils/trI18n';
import { Tooltip } from '@material-ui/core';
import PaperclipIcon from '@/component/common/icons/PaperclipIcon';
import { buildThumbnailFiles } from '../handler/attachThumbnailHandler';
import useAttachThumbnailData from '../handler/useAttachThumbnailData';
import useAttachThumbnailSize from '../handler/useAttachThumbnailSize';
import { THUMBNAIL_SIZE_CLIENT_CONTROL } from '@/component/common/icons/AttachUtils';
import AttachThumbnailModal from './AttachThumbnailModal';
import './AttachThumbnailList.css';

/**
 * Attachment thumbnail row for the ticket list.
 *
 * The per-file data/option/preview/download/logout logic lives in the shared
 * `useAttachThumbnailData` hook so the list and the "view all" modal reuse it. The list
 * slices to `Constant.ATTACH_FILES_LIMIT` and shows a "+N" button that opens the modal.
 * `panelType` is kept for the caller to indicate which panel this is in. `viewKey` is the list's
 * view key, which locates the list「翻訳版」toggle (CR-303134).
 */
function AttachThumbnailList({ ticketDetail, panelType, viewKey, options, attachBadge, projectInuseDetail }) {
    const thumbnailSize = useAttachThumbnailSize(THUMBNAIL_SIZE_CLIENT_CONTROL.TICKET_LIST);
    const { ownAttachments, chatPostFiles, renderThumbnail, dialogs } =
        useAttachThumbnailData({ ticketDetail, panelType, viewKey, isListContext: true, options, attachBadge, thumbnailSize, projectInuseDetail });

    // "+N" button opens the all-attachments modal.
    const [openModal, setOpenModal] = useState(false);

    const { files, remainingCount } = buildThumbnailFiles({
        attachments: ownAttachments,
        chatPostFiles,
        limit: Constant.ATTACH_FILES_LIMIT
    });

    return (
        <div className={`attach-thumbnail-list attach-thumbnail-list--${thumbnailSize}`}>
            <ul>
                {files.map((rawFile, index) => (
                    <li key={index}>{renderThumbnail(rawFile, index)}</li>
                ))}

                {remainingCount > 0 && (
                    <li className="attach-more-count-wrap">
                        <Tooltip
                            title={trI18n('SHOW_ALL_ATTACHMENTS')}
                            placement="top"
                            arrow={true}
                        >
                            <button
                                type="button"
                                className="attach-more-count"
                                onClick={() => setOpenModal(true)}
                            >
                                <PaperclipIcon width={14} height={15} />
                                <span className="attach-more-count-text">{`+ ${remainingCount}`}</span>
                            </button>
                        </Tooltip>
                    </li>
                )}
            </ul>

            {dialogs}

            {openModal && (
                <AttachThumbnailModal
                    ticketDetail={ticketDetail}
                    panelType={panelType}
                    viewKey={viewKey}
                    options={options}
                    attachBadge={attachBadge}
                    projectInuseDetail={projectInuseDetail}
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                />
            )}
        </div>
    );
}

AttachThumbnailList.propTypes = {
    ticketDetail: PropTypes.any,
    panelType: PropTypes.any,
    viewKey: PropTypes.string,
    options: PropTypes.func,
    attachBadge: PropTypes.func,
    projectInuseDetail: PropTypes.any
};

export default AttachThumbnailList;
